// The bundle email link (/auth/bundle/<bundle-id>?sig=…): signs the
// customer in as the bundle's owner and opens the bundle. Port of v1's
// AuthBundleView: POST /widget/bundle-email-auth { bundleId, sig,
// currentEmail } checks the signed link and mints a one-off magic-link
// grant, which the customer auth client redeems. Pure.

import { isValidDate } from "../../dates";

export type BundleGrant =
  | { status: "invalid_link" }
  | { status: "deleted" }
  | { status: "refunded"; email?: string }
  | { status: "expired"; email?: string; expiryDate?: string | null }
  | { status: "ok"; alreadyOwner: true; redirectTo: string }
  | { status: "ok"; ownerEmail: string; displayName: string; redirectTo: string; requiresSwitch: boolean; grantToken: string };

export type BundleFallback = { reason: "invalid_link" | "deleted" | "refunded" | "expired"; expiryDate?: string | null };

export type BundleGrantStep =
  /** Already signed in as the owner: open the bundle. */
  | { kind: "open" }
  /** Signed in as someone else: ask before switching. */
  | { kind: "switch"; currentEmail: string; ownerEmail: string; displayName: string; grantToken: string }
  /** Not signed in: redeem the grant. */
  | { kind: "verify"; grantToken: string }
  | ({ kind: "fallback" } & BundleFallback);

/** What to do with the API's answer. Anything unexpected reads as an
 * invalid link (as v1's catch did). */
export function grantStep(data: unknown, currentEmail: string | null): BundleGrantStep {
  const g = (data ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : undefined);
  switch (g.status) {
    case "deleted":
    case "refunded":
      return { kind: "fallback", reason: g.status };
    case "expired":
      return { kind: "fallback", reason: "expired", expiryDate: str(g.expiryDate) ?? null };
    case "ok": {
      if (g.alreadyOwner === true) return { kind: "open" };
      const grantToken = str(g.grantToken);
      if (!grantToken) break;
      if (g.requiresSwitch === true && currentEmail) {
        return {
          kind: "switch",
          currentEmail,
          ownerEmail: str(g.ownerEmail) ?? "the bundle’s owner",
          displayName: str(g.displayName) ?? "bundle",
          grantToken,
        };
      }
      return { kind: "verify", grantToken };
    }
  }
  return { kind: "fallback", reason: "invalid_link" };
}

/** The fallback's words, and where signing in from it leads: the bundle
 * itself when the link was only bad (the bundle page checks whose it is),
 * else the customer's account. */
export function fallbackCopy(f: BundleFallback): { headline: string; message: string; after: "bundle" | "profile" } {
  switch (f.reason) {
    case "deleted":
      return { headline: "This bundle is no longer available", message: "Sign in with your email to view your other purchases.", after: "profile" };
    case "expired":
      return {
        headline: isValidDate(f.expiryDate) ? `This bundle expired on ${formatExpiry(f.expiryDate)}` : "This bundle has expired",
        message: "Sign in to view your history.",
        after: "profile",
      };
    case "refunded":
      return { headline: "This bundle was refunded", message: "Sign in to view your bookings.", after: "profile" };
    default:
      return { headline: "Link invalid or expired", message: "This link is no longer valid. Sign in with your email to continue.", after: "bundle" };
  }
}

/** "12 March 2026". */
export const formatExpiry = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

/** The grant's session token from magic-link/verify's JSON, for when the
 * response's set-auth-token header didn't reach the client. */
export function verifiedToken(body: unknown): string | undefined {
  const t = (body as { token?: unknown } | null)?.token;
  return typeof t === "string" && t ? t : undefined;
}

export const SWITCH_FAILED = "Couldn't switch accounts. Please try the link again.";
